import React, { useCallback, useMemo } from 'react';
import { useLaborCostDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/store/laborCostDashboard.store';
import { DataTable } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/DataTable';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/LaborCostDashboard/LaborCostDashboard.styled';
import { DataTableColumn } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.types';
import { useAnalyticsFilterStore } from 'pages/ReportsPage/store/analyticsFilterView.context';

const COLUMNS: DataTableColumn[] = [
  { key: 'day', label: 'Day', width: '12%', noWrap: true },
  { key: 'location', label: 'Location', sortable: true, width: '23%' },
  { key: 'teamMember', label: 'Team Member', sortable: true, width: '23%' },
  {
    key: 'totalDailyWorkingHours',
    label: 'Total Daily Working Hours',
    align: 'center',
    width: '14%',
  },
  { key: 'dailyLimit', label: 'Daily Limit', align: 'center', width: '14%' },
  { key: 'dailyOvertime', label: 'Daily Overtime', align: 'center', width: '14%' },
];

export const DailyOvertimeTable: React.FC = () => {
  const data = useLaborCostDashboardStore((s) => s.dailyOvertimeDetails);
  const currentPage = useLaborCostDashboardStore((s) => s.dailyOvertimeDetailsPage);
  const totalPages = useLaborCostDashboardStore((s) => s.dailyOvertimeDetailsTotalPages);
  const isLoading = useLaborCostDashboardStore((s) => s.dailyOvertimeDetailsLoading);
  const hasError = useLaborCostDashboardStore((s) => s.dailyOvertimeDetailsError);
  const fetchDailyOvertimeDetails = useLaborCostDashboardStore((s) => s.fetchDailyOvertimeDetails);

  const dateRange = useAnalyticsFilterStore((s) => s.dateRange);
  const locationIds = useAnalyticsFilterStore((s) => s.locationIds);
  const positions = useAnalyticsFilterStore((s) => s.positions);
  const employmentTypes = useAnalyticsFilterStore((s) => s.employmentTypes);

  const handlePageChange = useCallback(
    (page: number) => {
      fetchDailyOvertimeDetails(page, dateRange, locationIds, positions, employmentTypes);
    },
    [fetchDailyOvertimeDetails, dateRange, locationIds, positions, employmentTypes],
  );

  const rows = useMemo(
    () =>
      data?.map((d) => ({
        day: d.day,
        location: d.location,
        teamMember: d.teamMember,
        totalDailyWorkingHours: d.totalDailyWorkingHours,
        dailyLimit: d.dailyLimit,
        dailyOvertime: d.dailyOvertime,
      })) ?? [],
    [data],
  );

  if (hasError) {
    return <EmptyStateContainer>Failed to load daily overtime data</EmptyStateContainer>;
  }

  if (!data && !isLoading) {
    return <EmptyStateContainer>No data available</EmptyStateContainer>;
  }

  return (
    <DataTable
      columns={COLUMNS}
      rows={rows}
      ariaLabel="Daily overtime details table"
      fixedLayout
      serverPagination={{
        currentPage,
        totalPages,
        onPageChange: handlePageChange,
        isLoading,
      }}
    />
  );
};
